import { useCallback, useEffect, useRef, useState } from "react";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";

/**
 * useState, but it starts from whatever this page showed last time.
 *
 * Every list page used to begin empty, show a loading skeleton, then swap in
 * the real rows — so moving around the app flickered and the page jumped as
 * its height changed. With this, a page paints its last-known content at
 * once and the fresh data replaces it quietly a moment later.
 *
 *   const [rows, setRows] = useRemembered("writers");
 *   setRows(data);            // show it and remember it
 *   setRows(data, false);     // show it, but don't remember (a filtered view)
 *
 * Remembered per workspace (see lib/cache.js), and wiped on sign-out.
 * Pass { owner: true } for owner-console pages, which belong to no workspace.
 */
export function useRemembered(name, { owner = false } = {}) {
  const { tenantId } = useTenant();
  const key = cacheKey(name, owner ? "owner" : tenantId);
  const [value, setValue] = useState(() => cacheGet(key) ?? null);
  const first = useRef(key);
  useEffect(() => {
    // switched workspace: show that workspace's remembered data, never the last one's
    if (first.current !== key) {
      first.current = key;
      setValue(cacheGet(key) ?? null);
    }
  }, [key]);
  const set = useCallback(
    (next, keep = true) => {
      if (typeof next === "function") {
        setValue((prev) => {
          const v = next(prev);
          if (keep) cacheSet(key, v);
          return v;
        });
      } else {
        setValue(next);
        if (keep) cacheSet(key, next);
      }
    },
    [key]
  );
  return [value, set];
}
